import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ImagePlus, Loader2, Play } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import postService, {
  DartPost,
  PostAuthorType,
  POST_AUTHOR_TYPE_LABELS,
} from '../../services/postService';
import EntityPicker, { AuthorOption, ComposerAuthor, authorOptionKey } from '../common/EntityPicker';
import { apiErrorMessage } from '../../services/api';
import { t } from '../../i18n';
import ImageCropperModal from '../ImageCropperModal';
import { blobToBase64 } from '../../utils/cropImage';
import { appAlert } from '../alerts/appAlert';
import { isVideoFile } from '../../utils/postMedia';
import useVideoDrafts, { VideoSelectionError } from '../../hooks/useVideoDrafts';

/** Medien im Composer in Reihenfolge (Feed-Videos 13.10.2026): Bild = Base64, Video = Draft-Key */
type ComposerItem = { kind: 'image'; dataUrl: string } | { kind: 'video'; key: string };

const ACCENT = '#3fced1';
const MAX_IMAGES = 10;
const MAX_CAPTION = 2200;
// Absender-Typen, Gruppierung und Konstanten leben seit 2026-09-19 im
// wiederverwendbaren EntityPicker (components/common) – hier re-exportiert,
// damit bestehende Importe (Tests, andere Komponenten) unverändert bleiben.
export type { AuthorOption, AuthorGroup, ComposerAuthor } from '../common/EntityPicker';
export {
  groupAuthorOptions,
  AUTHOR_GROUP_COLLAPSE_THRESHOLD,
  AUTHOR_GROUP_ORDER,
  AUTHOR_GROUP_LABELS,
} from '../common/EntityPicker';

interface PostComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (post: DartPost) => void;
  /** Absender vorbelegen (z.B. auf Detailseiten von Verein/Shop/…). */
  defaultAuthor?: ComposerAuthor | null;
}

function getEntityId(e: any): string | null {
  return (e?.id || e?._id) ?? null;
}

/**
 * Vollflächen-Dialog zum Erstellen eines Beitrags: Absender-Auswahl über ein
 * eigenes Panel (Suchfeld, nach Typ gruppiert, große Gruppen eingeklappt,
 * runde Avatare), Multi-Bild-Upload (min 1, max 10, Vorschau 4:5) und
 * Caption. Ohne Login → Redirect zu /login mit Rücksprung (Muster
 * ListingFollowButton). Flaches Design (fixed inset-0 über allem,
 * App-Style-Kopfzeile).
 */
const PostComposer: React.FC<PostComposerProps> = ({ isOpen, onClose, onCreated, defaultAuthor = null }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [authors, setAuthors] = useState<AuthorOption[]>([]);
  const [authorsLoading, setAuthorsLoading] = useState(false);
  const [selectedAuthorKey, setSelectedAuthorKey] = useState<string>('');

  const [items, setItems] = useState<ComposerItem[]>([]);
  const images = items; // Zählbasis (Bilder + Videos)
  const onVideoError = useCallback((code: VideoSelectionError, message?: string) => {
    if (code === 'too-long') appAlert.warning(t('web.component.post-composer.video-zu-lang'));
    else if (code === 'too-large') appAlert.warning(t('web.component.post-composer.video-zu-gross'));
    else appAlert.error(message || t('web.component.post-composer.video-upload-fehlgeschlagen'));
  }, []);
  const videos = useVideoDrafts(onVideoError);
  const [cropQueue, setCropQueue] = useState<string[]>([]);
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const authorKey = (a: ComposerAuthor) => authorOptionKey(a);

  // Absender laden: "Ich" + verwaltete Einträge (Muster EntriesManager/ProfilePage)
  const loadAuthors = useCallback(async () => {
    setAuthorsLoading(true);
    const options: AuthorOption[] = [];
    const myId = (user as any)?.id ?? null;
    if (myId) {
      options.push({
        authorType: 'PLAYER',
        authorId: myId,
        name: user?.username ?? null,
        image: (user as any)?.profileImage ?? null,
        label: `Ich (${user?.username ?? 'Spieler'})`,
      });
    }
    try {
      const [oC, oP, oSh, oT, oSe, aC, aP, aSh, aT, aSe, oL, oA, aL, aA] = await Promise.all([
        userService.getOwnedClubs(),
        userService.getOwnedPubs(),
        userService.getOwnedShops(),
        userService.getOwnedTournaments(),
        userService.getOwnedSeries(),
        userService.getAdminClubs(),
        userService.getAdminPubs(),
        userService.getAdminShops(),
        userService.getAdminTournaments(),
        userService.getAdminSeries(),
        userService.getOwnedLeagues(),
        userService.getOwnedAssociations(),
        userService.getAdminLeagues(),
        userService.getAdminAssociations(),
      ]);
      const push = (list: any[], type: PostAuthorType) => {
        (Array.isArray(list) ? list : []).forEach((e) => {
          const id = getEntityId(e);
          if (!id) return;
          const key = `${type}:${id}`;
          if (options.some((o) => authorKey(o) === key)) return;
          options.push({
            authorType: type,
            authorId: id,
            name: e?.name ?? null,
            image: e?.logo ?? e?.image ?? null,
            label: `${e?.name ?? 'Ohne Namen'} (${POST_AUTHOR_TYPE_LABELS[type]})`,
          });
        });
      };
      push(oC, 'CLUB');
      push(aC, 'CLUB');
      push(oP, 'PUB');
      push(aP, 'PUB');
      push(oSh, 'SHOP');
      push(aSh, 'SHOP');
      push(oT, 'TOURNAMENT');
      push(aT, 'TOURNAMENT');
      push(oSe, 'SERIES');
      push(aSe, 'SERIES');
      push(oL, 'LEAGUE');
      push(aL, 'LEAGUE');
      push(oA, 'ASSOCIATION');
      push(aA, 'ASSOCIATION');
    } catch (err) {
      console.error('Fehler beim Laden der verwalteten Einträge:', err);
    }
    // Vorbelegten Absender ergänzen, falls er nicht in den geladenen Listen steckt
    if (defaultAuthor?.authorId) {
      const key = authorKey(defaultAuthor);
      if (!options.some((o) => authorKey(o) === key)) {
        options.push({
          ...defaultAuthor,
          label: `${defaultAuthor.name ?? 'Ohne Namen'} (${POST_AUTHOR_TYPE_LABELS[defaultAuthor.authorType]})`,
        });
      }
    }
    setAuthors(options);
    setSelectedAuthorKey((prev) => {
      if (prev && options.some((o) => authorKey(o) === prev)) return prev;
      if (defaultAuthor?.authorId) return authorKey(defaultAuthor);
      return options.length > 0 ? authorKey(options[0]) : '';
    });
    setAuthorsLoading(false);
  }, [user, defaultAuthor]);

  useEffect(() => {
    if (!isOpen) return;
    // Ohne Login ist der Composer nicht nutzbar → Login mit Rücksprung
    // (Muster ListingFollowButton)
    if (!isAuthenticated) {
      onClose();
      navigate('/login', { state: { from: { pathname: location.pathname } } });
      return;
    }
    loadAuthors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAuthenticated]);

  // Scroll-Lock + ESC schließen
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  const selectedAuthor = useMemo(
    () => authors.find((o) => authorKey(o) === selectedAuthorKey) ?? null,
    [authors, selectedAuthorKey]
  );

  if (!isOpen || !isAuthenticated) return null;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      appAlert.warning(t('web.component.post-composer.maximal-bilder-pro-beitrag', { MAX_IMAGES }));
      return;
    }
    const selected = Array.from(files).slice(0, remaining);
    if (files.length > remaining) {
      appAlert.warning(t('web.component.post-composer.maximal-bilder-pro-beitrag-es', { MAX_IMAGES, remaining }));
    }
    // Videos (13.10.2026): sofort hochladen, Platz in der Reihenfolge reservieren
    const videoFiles = selected.filter((f) => isVideoFile(f));
    const imageFiles = selected.filter((f) => !isVideoFile(f));
    for (const vf of videoFiles) {
      // Kachel sofort (13.10.2026), Prüfung/Upload im Hintergrund; bei Ablehnung Platz wieder frei
      const key = videos.addFile(vf, (k) => setItems((prev) => prev.filter((it) => !(it.kind === 'video' && it.key === k))));
      setItems((prev) => [...prev, { kind: 'video' as const, key }].slice(0, MAX_IMAGES));
    }
    // Bildeditor je Bild, fest 4:5 wie die Feed-Darstellung (Marcel 2026-09-17):
    // Dateien in eine Warteschlange, der Editor arbeitet sie nacheinander ab.
    if (imageFiles.length > 0) setCropQueue((prev) => [...prev, ...imageFiles.map((f) => URL.createObjectURL(f))]);
  };

  const shiftCropQueue = () => {
    setCropQueue((prev) => {
      if (prev[0]) URL.revokeObjectURL(prev[0]);
      return prev.slice(1);
    });
  };
  const handleCropDone = async (blob: Blob) => {
    try {
      const dataUrl = await blobToBase64(blob, { dataUrl: true });
      setItems((prev) => [...prev, { kind: 'image' as const, dataUrl }].slice(0, MAX_IMAGES));
    } catch {
      appAlert.error(t('web.component.post-composer.bild-konnte-nicht-gelesen-werden'));
    } finally {
      shiftCropQueue();
    }
  };

  const removeImage = (idx: number) => {
    const it = items[idx];
    if (it?.kind === 'video') videos.remove(it.key);
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const author = selectedAuthor;
    if (!author) {
      appAlert.warning(t('web.component.post-composer.bitte-waehle-einen-absender-aus'));
      return;
    }
    if (items.length === 0) {
      appAlert.warning(t('web.component.post-composer.bitte-fuege-mindestens-ein-medium'));
      return;
    }
    if (videos.uploading) {
      appAlert.warning(t('web.component.post-composer.bitte-warten-upload'));
      return;
    }
    setSubmitting(true);
    try {
      const media = items.map((it) => {
        if (it.kind === 'image') return { type: 'image' as const, data: it.dataUrl };
        const d = videos.drafts.find((x) => x.key === it.key);
        return d?.videoId ? { type: 'video' as const, videoId: d.videoId } : null;
      }).filter((m): m is NonNullable<typeof m> => m != null);
      const created = await postService.createPost({
        authorType: author.authorType,
        authorId: author.authorId,
        media,
        caption: caption.trim() || undefined,
      });
      if (created) onCreated?.(created);
      setItems([]);
      setCaption('');
      onClose();
    } catch (err: any) {
      appAlert.error(apiErrorMessage(err, t('web.component.post-composer.beitrag-konnte-nicht-erstellt-werden')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950 overflow-y-auto" role="dialog" aria-label={t('web.component.post-composer.beitrag-erstellen')}>
      {/* App-Style-Kopfzeile (Vollbild über allem) */}
      <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-[600px] mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('web.component.create-entry-modal.schliessen')}
            className="p-2 -ml-2 rounded-full text-zinc-300 hover:text-white hover:bg-zinc-800 transition"
          >
            <X size={20} />
          </button>
          <h1 className="text-base font-bold text-white flex-1">{t('web.component.post-composer.beitrag-erstellen')}</h1>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || items.length === 0 || videos.uploading}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-black disabled:opacity-40 transition"
            style={{ backgroundColor: ACCENT }}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={15} className="animate-spin" />
                {t('web.component.post-composer.posten')}
              </span>
            ) : (
              t('web.component.post-composer.posten-2')
            )}
          </button>
        </div>
      </div>

      <div className="max-w-[600px] mx-auto px-4 py-6 space-y-6">
        {/* Absender: Auswahlfeld mit Suche, gruppiert (EntityPicker, Einzelauswahl) */}
        <EntityPicker
          options={authors}
          value={selectedAuthorKey}
          onChange={(v) => setSelectedAuthorKey(typeof v === 'string' ? v : v[0] ?? '')}
          multiple={false}
          label={t('web.component.post-composer.posten-als')}
          placeholder={t('web.component.post-composer.absender-waehlen-2')}
          ariaLabel={t('web.component.post-composer.absender-waehlen')}
          loading={authorsLoading}
          loadingText={t('web.component.post-composer.absender-werden-geladen')}
          searchPlaceholder={t('web.component.post-composer.absender-suchen')}
          emptyText={t('web.component.post-composer.keine-absender-gefunden')}
          testId="author-picker"
        />

        {/* Bilder */}
        <div>
          <p className="text-sm font-medium text-zinc-300 mb-1.5">
            {t('web.component.post-composer.medien')}{' '}<span className="text-zinc-500">{t('web.component.post-composer.mindestens-1-medium', { length: items.length, MAX_IMAGES })}</span>
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {items.map((it, idx) => {
              if (it.kind === 'video') {
                const d = videos.drafts.find((x) => x.key === it.key);
                return (
                  <div key={it.key} className="relative aspect-[4/5] rounded-xl overflow-hidden bg-black" data-testid="composer-video-tile" data-status={d?.status ?? 'uploading'}>
                    {d?.poster ? (
                      <img src={postService.posterUrl(d.poster) ?? ''} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <video src={d?.previewUrl} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/45 text-white">
                      {d?.status === 'ready' ? <Play size={20} fill="currentColor" /> : <Loader2 size={18} className="animate-spin" />}
                      <span className="text-[10px] font-semibold text-center px-1">
                        {d?.status === 'checking' ? t('web.component.post-composer.video-wird-geprueft')
                          : d?.status === 'uploading' ? t('web.component.post-composer.video-wird-hochgeladen', { percent: d.progress })
                          : d?.status === 'processing' ? t('web.component.post-composer.video-wird-verarbeitet')
                          : d?.status === 'failed' ? t('web.component.post-video.verarbeitung-fehlgeschlagen') : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      aria-label={t('web.component.post-composer.video-entfernen', { idx: idx + 1 })}
                      className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white hover:bg-black/90 transition"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              }
              return (
              <div key={idx} className="relative aspect-[4/5] rounded-xl overflow-hidden bg-zinc-900">
                <img src={it.dataUrl} alt={t('web.component.post-composer.vorschau', { idx: idx + 1 })} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  aria-label={t('web.component.post-composer.bild-entfernen', { idx: idx + 1 })}
                  className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white hover:bg-black/90 transition"
                >
                  <X size={14} />
                </button>
              </div>
              );
            })}
            {items.length < MAX_IMAGES && (
              <label className="aspect-[4/5] rounded-xl border border-dashed border-zinc-700 hover:border-[#3fced1] flex flex-col items-center justify-center gap-1.5 cursor-pointer text-zinc-500 hover:text-zinc-300 transition">
                <ImagePlus size={22} style={{ color: ACCENT }} />
                <span className="text-[11px] font-medium">{t('web.component.listing-admins-card.hinzufuegen')}</span>
                <ImageCropperModal
                  open={cropQueue.length > 0}
                  imageSrc={cropQueue[0] ?? null}
                  aspect="portrait"
                  title={t('web.component.post-composer.bild-zuschneiden')}
                  onCancel={shiftCropQueue}
                  onDone={handleCropDone}
                />
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  data-testid="post-image-input"
                  onChange={(e) => {
                    handleFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </div>

        {/* Caption */}
        <div>
          <label htmlFor="post-caption" className="block text-sm font-medium text-zinc-300 mb-1.5">
            {t('web.component.post-composer.bildunterschrift')}{' '}<span className="text-zinc-500">(optional)</span>
          </label>
          <textarea
            id="post-caption"
            value={caption}
            maxLength={MAX_CAPTION}
            onChange={(e) => setCaption(e.target.value)}
            rows={4}
            placeholder={t('web.component.post-composer.schreibe-etwas-zu-deinem-beitrag')}
            className="w-full rounded-xl bg-zinc-900 border border-zinc-800 text-white text-sm px-3.5 py-2.5 resize-y focus:outline-none focus:border-[#3fced1]"
          />
          <p className="mt-1 text-right text-[11px] text-zinc-600">
            {caption.length}/{MAX_CAPTION}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PostComposer;
